/**
 * Client-side helpers for the download landing page + influencer short links.
 * Keeps a stable anonymous session id and the last-seen campaign so the visit
 * and store-redirect events can be de-duplicated and attributed server-side.
 */

const SESSION_KEY = 'ap_mkt_session';
const CAMPAIGN_KEY = 'ap_mkt_campaign';
const CAMPAIGN_COOKIE = 'ap_campaign';
const CAMPAIGN_TTL_DAYS = 30;

export type DevicePlatform = 'IOS' | 'ANDROID' | 'DESKTOP';

function randomId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Stable per-browser session id, generated once and persisted. */
export function getSessionId(): string {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = randomId();
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    // Private mode / storage disabled — fall back to an ephemeral id.
    return randomId();
  }
}

/** Persist the campaign slug in both localStorage and a cookie. */
export function storeCampaign(slug: string): void {
  const value = slug.toLowerCase();
  try {
    localStorage.setItem(CAMPAIGN_KEY, value);
  } catch {
    /* ignore */
  }
  const expires = new Date(
    Date.now() + CAMPAIGN_TTL_DAYS * 24 * 60 * 60 * 1000,
  ).toUTCString();
  document.cookie = `${CAMPAIGN_COOKIE}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name + '=([^;]*)'),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Resolve the active campaign for the download page: the ?campaign= query param
 * wins, then localStorage, then the cookie.
 */
export function resolveCampaign(searchParam?: string | null): string | null {
  if (searchParam) return searchParam.toLowerCase();
  try {
    const stored = localStorage.getItem(CAMPAIGN_KEY);
    if (stored) return stored;
  } catch {
    /* ignore */
  }
  return readCookie(CAMPAIGN_COOKIE);
}

/** Coarse device detection for choosing the store redirect. */
export function detectPlatform(): DevicePlatform {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'IOS';
  if (/Android/i.test(ua)) return 'ANDROID';
  return 'DESKTOP';
}

/** Respect Do-Not-Track for the (anonymised) analytics beacons. */
export function isTrackingAllowed(): boolean {
  const dnt =
    navigator.doNotTrack ||
    (window as unknown as { doNotTrack?: string }).doNotTrack;
  return dnt !== '1' && dnt !== 'yes';
}
